import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { EvChargeScheduleData } from 'clipsal-cortex-types/src/api/api-evcharger';

import { RootState } from '../../../app/store';
import { del, get, post, put } from '../../../common/api/api-helpers';
import {
  trackDynatraceScheduleOperationResult,
  type DynatraceScheduleInput,
  type DynatraceScheduleOperation,
} from '../../../utils/native/dynatrace-events';
import { Schedule } from '../types';
import { mapAPISchedules } from './scheduler-helper';

export type SchedulerState = {
  schedules: Schedule[];
  timeEditType: 'start' | 'end' | null; // Controls whether we're editing the start or end time.
  isLoaded: boolean;
  isError: boolean;
  isSaving: boolean;
};

export const initialState: SchedulerState = {
  isLoaded: false,
  isError: false,
  isSaving: false,
  timeEditType: null,
  schedules: [],
};

async function runTrackedScheduleOperation<T>(
  operation: DynatraceScheduleOperation,
  chargerId: string,
  scheduleId: string | undefined,
  request: () => Promise<T>,
  trackingOptions: {
    scheduleInput?: DynatraceScheduleInput;
    resolveScheduleId?: (result: T) => string | undefined;
  } = {}
): Promise<T> {
  const startedAt = Date.now();
  try {
    const result = await request();
    trackDynatraceScheduleOperationResult({
      operation,
      outcome: 'success',
      chargerId,
      scheduleId: trackingOptions.resolveScheduleId?.(result) ?? scheduleId,
      scheduleInput: trackingOptions.scheduleInput,
      startedAt,
    });
    return result;
  } catch (error) {
    trackDynatraceScheduleOperationResult({
      operation,
      outcome: 'failure',
      chargerId,
      scheduleId,
      scheduleInput: trackingOptions.scheduleInput,
      startedAt,
    });
    throw error;
  }
}

export const fetchEvChargerSchedules = createAsyncThunk<EvChargeScheduleData[], string, { state: RootState }>(
  'evCharger/fetchEvChargerSchedules',
  async (siteDeviceId) =>
    runTrackedScheduleOperation('read', siteDeviceId, undefined, () =>
      get<EvChargeScheduleData[]>(`/v1/ev_chargers/${siteDeviceId}/schedules`)
    )
);

function buildScheduleBody(schedule: Partial<Schedule>, siteDeviceId: string, groupId?: string | null) {
  return [
    schedule.startTime && {
      ...(groupId ? { group_uuid: groupId } : {}),
      site_device_id: siteDeviceId,
      name: schedule.name,
      weekly_freq_interval: schedule.daysOfWeek,
      event_time: schedule.startTime,
      event_action: 'switch_on',
    },
    schedule.endTime && {
      ...(groupId ? { group_uuid: groupId } : {}),
      site_device_id: siteDeviceId,
      name: schedule.name,
      weekly_freq_interval: schedule.daysOfWeek,
      event_time: schedule.endTime,
      event_action: 'switch_off',
    },
  ].filter(Boolean);
}

export const addEvChargerSchedule = createAsyncThunk<
  Schedule,
  { siteDeviceId: string; newSchedule: Partial<Schedule> },
  { state: RootState }
>('evCharger/addEvChargerSchedule', async ({ siteDeviceId, newSchedule }) =>
  runTrackedScheduleOperation(
    'create',
    siteDeviceId,
    undefined,
    async () => {
      const body = buildScheduleBody(newSchedule, siteDeviceId);
      const response = await post<EvChargeScheduleData[]>(`/v1/ev_chargers/${siteDeviceId}/schedules`, body);
      const schedule = mapAPISchedules(response)[0];
      if (!schedule) {
        throw new Error('Failed to create schedule');
      }
      return schedule;
    },
    {
      scheduleInput: newSchedule,
      resolveScheduleId: (schedule) => schedule.groupId ?? undefined,
    }
  )
);

export const editEvChargerSchedule = createAsyncThunk<
  Schedule,
  { siteDeviceId: string; editedSchedule: Partial<Schedule> },
  { state: RootState }
>('evCharger/editEvChargerSchedule', async ({ siteDeviceId, editedSchedule }) =>
  runTrackedScheduleOperation(
    'update',
    siteDeviceId,
    editedSchedule.groupId ?? undefined,
    async () => {
      const body = buildScheduleBody(editedSchedule, siteDeviceId, editedSchedule.groupId);
      const response = await put<EvChargeScheduleData[]>(`/v1/ev_chargers/${siteDeviceId}/schedules`, body);
      const schedule = mapAPISchedules(response)[0];
      if (!schedule) {
        throw new Error('Failed to update schedule');
      }
      return schedule;
    },
    { scheduleInput: editedSchedule }
  )
);

export const deleteEvChargerSchedule = createAsyncThunk<
  string,
  { siteDeviceId: string; groupId: string },
  { state: RootState }
>('evCharger/deleteEvChargerSchedule', async ({ siteDeviceId, groupId }) =>
  runTrackedScheduleOperation('delete', siteDeviceId, groupId, async () => {
    await del<void>(`/v1/ev_chargers/${siteDeviceId}/schedules?group_uuid=${groupId}`, {
      site_device_id: siteDeviceId,
    });
    return groupId;
  })
);

export const evChargerSchedulerSlice = createSlice({
  name: 'evChargerScheduler',
  initialState,
  reducers: {
    resetSchedulerData: () => initialState,
    setSchedulerState: (state, action: PayloadAction<Partial<SchedulerState>>) => {
      return { ...state, ...action.payload };
    },
    addSchedule: (state, action: { payload: Schedule }) => {
      state.schedules = [...state.schedules, action.payload];
    },
    editSchedule: (state, action: { payload: string }) => {
      state.schedules = state.schedules.map((schedule) =>
        schedule.groupId === action.payload ? { ...schedule, isEditing: true } : schedule
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvChargerSchedules.fulfilled, (state, { payload }) => {
        state.schedules = mapAPISchedules(payload);
        state.isLoaded = true;
      })
      .addCase(fetchEvChargerSchedules.rejected, (state) => {
        state.isLoaded = true;
        state.isError = true;
      })
      .addCase(addEvChargerSchedule.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(addEvChargerSchedule.fulfilled, (state, { payload }) => {
        // Replace the schedule which is being edited with the newly created schedule from the API.
        state.schedules = state.schedules.map((schedule) =>
          schedule.isEditing ? { ...payload, isEditing: false } : schedule
        );
        state.isSaving = false;
      })
      .addCase(addEvChargerSchedule.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(editEvChargerSchedule.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(editEvChargerSchedule.fulfilled, (state, { payload }) => {
        // Replace the schedule which is being edited with the updated schedule from the API.
        state.schedules = state.schedules.map((schedule) =>
          schedule.isEditing ? { ...payload, isEditing: false } : schedule
        );
        state.isSaving = false;
      })
      .addCase(editEvChargerSchedule.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(deleteEvChargerSchedule.fulfilled, (state, { payload }) => {
        state.schedules = state.schedules.filter((schedule) => schedule.groupId !== payload);
      });
  },
});

export const { addSchedule, editSchedule, resetSchedulerData, setSchedulerState } = evChargerSchedulerSlice.actions;

export const selectEvChargerSchedules = (state: RootState) => {
  return state.evChargerScheduler;
};

export const selectEvChargerSchedule = (groupId: string) => (state: RootState) => {
  return state.evChargerScheduler.schedules.find((schedule) => schedule.groupId === groupId) || null;
};

export const { reducer: evChargerSchedulerReducer } = evChargerSchedulerSlice;
